import { Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { ExternalLink, TextareaControl } from "@wordpress/components";
import { __ } from "@wordpress/i18n";
import React from "react";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import Panel from "../../../components/common/Panel";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import {
	TextAlignCenter,
	TextAlignJustify,
	TextAlignLeft,
	TextAlignRight,
} from "../../../components/icons/Icons";
import {
	Border,
	BoxShadow,
	Media,
	PopoverDrawer,
	Select,
	Slider,
	Toggle,
	ToggleButton,
	ToggleButtonGroup,
	UrlInput,
} from "../../../controls";
import {
	CONTENT_POSITION_OPTIONS,
	HOVER_IMAGE_OPTIONS,
	IMAGE_FILTER_OPTIONS,
	MASK_POSITION,
	MASK_REPEAT,
	MASK_SHAPE,
	MASK_SIZE,
	OBJECT_FIT_OPTIONS,
	SEPARATOR_POSITION_OPTIONS,
	SEPARATOR_STYLE_OPTIONS,
} from "../constants";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			link,
			image = {},
			alignment,
			objectFit,
			border,
			hoverBorder,
			boxShadow,
			boxShadowHover,
			blockMargin,
			blockPadding,
			blockZIndex,
			cssID,
			width,
			height,
			maxWidth,
			opacity,
			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,
			className,
			overlay,
			enableCaption,
			imageFilter,
			hoverEffect,
			showSeparatorOn,
			separatorStyle,
			separatorPosition,
			overlayContentPosition,
			overlayBorder,
			showHeadingOn,
			showDescription,
			maskShape,
			maskPosition,
			maskSize,
			maskRepeat,
		},
		setAttributes,
	} = props;
	return (
		<>
			<InspectorTabs />
			<GeneralInspectorControls>
				<Panel title={__("Layout", "magazine-blocks")} initialOpen>
					<Slider
						label={__("Width", "magazine-blocks")}
						value={width}
						min={0}
						max={1920}
						step={1}
						onChange={(val: any) => setAttributes({ width: val })}
						resetAttributeKey="width"
						responsive
						units={["px", "em", "%"]}
					/>
					<Slider
						label={__("Max Width", "magazine-blocks")}
						value={maxWidth}
						min={0}
						max={1920}
						step={1}
						onChange={(val: any) =>
							setAttributes({ maxWidth: val })
						}
						resetAttributeKey="maxWidth"
						responsive
						units={["px", "em", "%"]}
					/>
					<Slider
						label={__("Height", "magazine-blocks")}
						value={height}
						min={0}
						max={1000}
						step={1}
						onChange={(val: any) => setAttributes({ height: val })}
						resetAttributeKey="height"
						responsive
						units={["px", "em", "%"]}
					/>
				</Panel>
				<Panel title={__("Image", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Add Image", "magazine-blocks")}
						popoverDivProps={{
							sx: {
								".components-popover__content": {
									p: 4,
									width: "280px",
								},
							},
						}}
					>
						<Media
							onChange={(val) => setAttributes({ image: val })}
							label={__("Image", "magazine-blocks")}
							type={"image"}
							value={image}
						/>
						<UrlInput
							label={__("URL", "magazine-blocks")}
							onChange={(val) => setAttributes({ link: val })}
							value={link}
							placeholder="https://"
							newTab
						/>
						<TextareaControl
							label={__("Alt", "magazine-blocks")}
							className="mzb-control mzb-textarea"
							value={image?.alt ?? ""}
							onChange={(val) =>
								setAttributes({
									image: { ...(image ?? {}), alt: val },
								})
							}
							help={
								<>
									<ExternalLink href="https://www.w3.org/WAI/tutorials/images/decision-tree">
										{__(
											"Describe the purpose of the image",
											"magazine-blocks"
										)}
									</ExternalLink>
									<br />
									{__(
										"Leave empty if the image is purely decorative.",
										"magazine-blocks"
									)}
								</>
							}
						/>
					</PopoverDrawer>
					<Toggle
						checked={enableCaption ?? false}
						onChange={(v) => setAttributes({ enableCaption: v })}
						label={__("Enable Caption", "magazine-blocks")}
					/>
				</Panel>
				<Panel title={__("Overlay", "magazine-blocks")}>
					<Toggle
						checked={overlay ?? false}
						onChange={(v) => setAttributes({ overlay: v })}
						label={__("Enable", "magazine-blocks")}
					/>
					{overlay && (
						<>
							<Select
								options={CONTENT_POSITION_OPTIONS}
								onChange={(val) =>
									setAttributes({
										overlayContentPosition: val,
									})
								}
								value={overlayContentPosition}
								label={__(
									"Content Position",
									"magazine-blocks"
								)}
							/>
							<Border
								value={overlayBorder}
								onChange={(v) => {
									setAttributes({
										overlayBorder: v,
									});
								}}
								resetAttributeKey="overlayBorder"
							/>
							<ToggleButtonGroup
								value={showSeparatorOn}
								onChange={(v) => {
									setAttributes({ showSeparatorOn: v });
								}}
								label={__(
									"Show Separator On",
									"magazine-blocks"
								)}
								resetAttributeKey="showSeparatorOn"
								groupProps={{
									sx: {
										".chakra-button": {
											minH: "32px",
											color: "#1A202C",
										},
										"&[data-active]": {
											color: "#fff",
										},
									},
								}}
							>
								<ToggleButton value="always">
									{__("Always", "magazine-blocks")}
								</ToggleButton>
								<ToggleButton value="hover">
									{__("Hover", "magazine-blocks")}
								</ToggleButton>
							</ToggleButtonGroup>
							<Select
								onChange={(val) =>
									setAttributes({ separatorStyle: val })
								}
								value={separatorStyle}
								label={__("Separator Style", "magazine-blocks")}
								options={SEPARATOR_STYLE_OPTIONS}
							/>
							<Select
								onChange={(val) =>
									setAttributes({
										separatorPosition: val,
									})
								}
								value={separatorPosition}
								label={__(
									"Separator Position",
									"magazine-blocks"
								)}
								options={SEPARATOR_POSITION_OPTIONS}
							/>
							<ToggleButtonGroup
								value={showHeadingOn}
								onChange={(v) => {
									setAttributes({ showHeadingOn: v });
								}}
								label={__("Show Heading", "magazine-blocks")}
								resetAttributeKey="showHeadingOn"
								groupProps={{
									sx: {
										".chakra-button": {
											minH: "32px",
											color: "#1A202C",
										},
										"&[data-active]": {
											color: "#fff",
										},
									},
								}}
							>
								<ToggleButton value="always">
									{__("Always", "magazine-blocks")}
								</ToggleButton>
								<ToggleButton value="hover">
									{__("Hover", "magazine-blocks")}
								</ToggleButton>
							</ToggleButtonGroup>
							<ToggleButtonGroup
								value={showDescription}
								onChange={(v) => {
									setAttributes({ showDescription: v });
								}}
								label={__(
									"Show Description",
									"magazine-blocks"
								)}
								resetAttributeKey="showDescription"
								groupProps={{
									sx: {
										".chakra-button": {
											minH: "32px",
											color: "#1A202C",
											svg: {
												w: 6,
												h: 6,
											},
										},
										"&[data-active]": {
											color: "#fff",
										},
									},
								}}
							>
								<ToggleButton value="always">
									{__("Always", "magazine-blocks")}
								</ToggleButton>
								<ToggleButton value="hover">
									{__("Hover", "magazine-blocks")}
								</ToggleButton>
							</ToggleButtonGroup>
						</>
					)}
				</Panel>
				<Panel title={__("Mask", "magazine-blocks")}>
					<Select
						label={__("Mask Shape", "magazine-blocks")}
						value={maskShape}
						options={MASK_SHAPE}
						onChange={(newShape) =>
							setAttributes({ maskShape: newShape })
						}
					/>
					{maskShape && maskShape !== "none" && (
						<>
							<Select
								label={__("Mask Size", "magazine-blocks")}
								value={maskSize}
								options={MASK_SIZE}
								onChange={(newSize) =>
									setAttributes({ maskSize: newSize })
								}
							/>
							<Select
								label={__("Mask Position", "magazine-blocks")}
								value={maskPosition}
								options={MASK_POSITION}
								onChange={(newPosition) =>
									setAttributes({
										maskPosition: newPosition,
									})
								}
							/>
							<Select
								label={__("Mask Repeat", "magazine-blocks")}
								value={maskRepeat}
								options={MASK_REPEAT}
								onChange={(val) =>
									setAttributes({ maskRepeat: val })
								}
							/>
						</>
					)}
				</Panel>
			</GeneralInspectorControls>
			<StyleInspectorControls>
				<Panel title={__("Properties", "magazine-blocks")} initialOpen>
					<ToggleButtonGroup
						value={alignment}
						onChange={(v) => {
							setAttributes({ alignment: v });
						}}
						label={__("Alignment", "magazine-blocks")}
						responsive
						resetAttributeKey="alignment"
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									"&[data-active]": {
										svg: {
											fill: "#fff !important",
										},
									},
								},
							},
						}}
					>
						<ToggleButton value="left">
							<TextAlignLeft />
						</ToggleButton>
						<ToggleButton value="center">
							<TextAlignCenter />
						</ToggleButton>
						<ToggleButton value="right">
							<TextAlignRight />
						</ToggleButton>
						<ToggleButton value="justify">
							<TextAlignJustify />
						</ToggleButton>
					</ToggleButtonGroup>
				</Panel>
				<Panel title={__("Image", "magazine-blocks")}>
					<Select
						onChange={(val) => setAttributes({ objectFit: val })}
						value={objectFit}
						label={__("Object Fit", "magazine-blocks")}
						options={OBJECT_FIT_OPTIONS}
					/>
					<Select
						onChange={(val) => setAttributes({ hoverEffect: val })}
						value={hoverEffect}
						label={__("On Hover Image", "magazine-blocks")}
						options={HOVER_IMAGE_OPTIONS}
					/>
					<Select
						onChange={(val) => setAttributes({ imageFilter: val })}
						value={imageFilter}
						label={__("Image Filter", "magazine-blocks")}
						options={IMAGE_FILTER_OPTIONS}
					/>
					<Slider
						onChange={(val) => setAttributes({ opacity: val })}
						label={__("Opacity", "magazine-blocks")}
						step={0.1}
						min={0}
						max={1}
						value={opacity}
						resetAttributeKey="opacity"
					/>
				</Panel>
				<Panel title={__("Border", "magazine-blocks")}>
					<Tabs variant="border">
						<TabList>
							<Tab>{__("Normal", "magazine-blocks")}</Tab>
							<Tab>{__("Hover", "magazine-blocks")}</Tab>
						</TabList>
						<TabPanels>
							<TabPanel>
								<Border
									value={border}
									onChange={(v) => {
										setAttributes({
											border: v,
										});
									}}
									resetAttributeKey="border"
								/>
								<BoxShadow
									resetAttributeKey="boxShadow"
									value={boxShadow}
									onChange={(val) =>
										setAttributes({
											boxShadow: val,
										})
									}
								/>
							</TabPanel>
							<TabPanel>
								<Border
									value={hoverBorder}
									onChange={(v) => {
										setAttributes({
											hoverBorder: v,
										});
									}}
									resetAttributeKey="hoverBorder"
								/>
								<BoxShadow
									resetAttributeKey="boxShadowHover"
									value={boxShadowHover}
									onChange={(val) =>
										setAttributes({
											boxShadowHover: val,
										})
									}
								/>
							</TabPanel>
						</TabPanels>
					</Tabs>
				</Panel>
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
					blockZIndex={blockZIndex}
					cssID={cssID}
					className={className}
					// hasblockHTML={false}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
